import { LOCIZE_NS, MUTATION_KEYS } from '@avanta/shared/constants/common';
import { ErrorService } from '@avanta/shared/services/error';
import { openPDF } from '@avanta/shared/utils/storage';
import { useMutation } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { EnterprisesService } from 'services/enterprises';
import { useUserStore } from 'stores/user';
import { type BillingInvoice } from '../types';

const EMPTY_PDF_STATUS = 424;

export const useDownloadInvoicePdf = (
  invoice: BillingInvoice
): { downloadPdf: () => void; isPending: boolean } => {
  const { t } = useTranslation(LOCIZE_NS.INSURANCE);
  const insuranceId = useUserStore(state => state.user?.id) ?? '';

  const { mutate, isPending } = useMutation({
    mutationKey: [MUTATION_KEYS.DOWNLOAD_ENTERPRISE_INVOICE_PDF, invoice.id],
    mutationFn: async () => {
      const { data } = await EnterprisesService.getInvoicePdf(insuranceId, invoice.id);
      await openPDF({ file: data, filename: invoice.number });
    },
    onError: error => {
      if (isAxiosError(error) && error.response?.status === EMPTY_PDF_STATUS) {
        toast.error(
          t('BillingPage.viewDocument.empty', {
            invoiceNumber: invoice.number,
          })
        );
        return;
      }

      if (isAxiosError(error)) {
        ErrorService.handlerErrorRequest(error);
      }
    },
  });

  return {
    downloadPdf: () => mutate(),
    isPending,
  };
};
